// Rhythmic values. Ticks use PPQ = 960 (ticks per quarter note).

export const PPQ = 960;
export const WHOLE = PPQ * 4;

export type DurBase = 1 | 2 | 4 | 8 | 16 | 32 | 64;
export const DUR_BASES: readonly DurBase[] = [1, 2, 4, 8, 16, 32, 64];

export interface Tuplet {
  actual: number; // notes played…
  normal: number; // …in the time of this many (triplet = 3:2)
}

export interface Duration {
  base: DurBase;
  dots: 0 | 1 | 2;
  tuplet?: Tuplet;
}

export interface TimeSig {
  num: number;
  den: DurBase;
  symbol?: 'common' | 'cut';
}

export function dur(base: DurBase, dots: 0 | 1 | 2 = 0, tuplet?: Tuplet): Duration {
  return tuplet ? { base, dots, tuplet } : { base, dots };
}

/** Length in ticks. */
export function ticks(d: Duration): number {
  const plain = (WHOLE / d.base) * (2 - 1 / 2 ** d.dots);
  return d.tuplet ? (plain * d.tuplet.normal) / d.tuplet.actual : plain;
}

export function barCapacity(ts: TimeSig): number {
  return (WHOLE / ts.den) * ts.num;
}

export function durationEquals(a: Duration, b: Duration): boolean {
  return (
    a.base === b.base &&
    a.dots === b.dots &&
    (a.tuplet?.actual ?? 0) === (b.tuplet?.actual ?? 0) &&
    (a.tuplet?.normal ?? 0) === (b.tuplet?.normal ?? 0)
  );
}

/** Next shorter base value (quarter → eighth). Returns the same duration at 64th. */
export function shorter(d: Duration): Duration {
  const i = DUR_BASES.indexOf(d.base);
  return { ...d, base: DUR_BASES[Math.min(i + 1, DUR_BASES.length - 1)] };
}

/** Next longer base value (quarter → half). Returns the same duration at whole. */
export function longer(d: Duration): Duration {
  const i = DUR_BASES.indexOf(d.base);
  return { ...d, base: DUR_BASES[Math.max(i - 1, 0)] };
}

const BASE_NAMES: Record<DurBase, string> = {
  1: 'whole',
  2: 'half',
  4: 'quarter',
  8: 'eighth',
  16: '16th',
  32: '32nd',
  64: '64th',
};

export function durationName(d: Duration): string {
  const dots = d.dots === 2 ? 'double-dotted ' : d.dots === 1 ? 'dotted ' : '';
  const tup = d.tuplet ? (d.tuplet.actual === 3 && d.tuplet.normal === 2 ? ' triplet' : ` (${d.tuplet.actual}:${d.tuplet.normal})`) : '';
  return dots + BASE_NAMES[d.base] + tup;
}

/** Exact non-tuplet duration for a tick count, if one exists. */
export function durationForTicks(t: number): Duration | null {
  for (const base of DUR_BASES) {
    for (const dots of [0, 1, 2] as const) {
      const d = { base, dots };
      if (Math.abs(ticks(d) - t) < 1e-6) return d;
    }
  }
  return null;
}

/**
 * Split a tick span into plain durations, largest first (used to fill rests
 * and to import arbitrary lengths). Remainders shorter than a 64th are dropped.
 */
export function splitTicks(t: number, allowDots = true): Duration[] {
  const out: Duration[] = [];
  let left = Math.round(t);
  const min = WHOLE / 64;
  while (left >= min - 1e-6) {
    let best: Duration | null = null;
    for (const base of DUR_BASES) {
      for (const dots of allowDots ? ([1, 0] as const) : ([0] as const)) {
        const d: Duration = { base, dots };
        if (ticks(d) <= left + 1e-6) {
          if (!best || ticks(d) > ticks(best)) best = d;
        }
      }
    }
    if (!best) break;
    out.push(best);
    left -= ticks(best);
  }
  return out;
}

/** Beat unit (in ticks) a listener feels: dotted quarter in compound meters, else 1/den. */
export function beatTicks(ts: TimeSig): number {
  const compound = ts.den >= 8 && ts.num % 3 === 0 && ts.num > 3;
  return compound ? (WHOLE / ts.den) * 3 : WHOLE / ts.den;
}

/**
 * Beam/beat grouping for a time signature as tick boundaries within the bar.
 * 4/4 → quarter beats; 6/8 → dotted quarters; 2/2 → halves; 5/8 → 3+2.
 */
export function beatGroups(ts: TimeSig): number[] {
  const unit = WHOLE / ts.den;
  const groups: number[] = [];
  if (ts.den >= 8 && ts.num % 3 === 0 && ts.num > 3) {
    for (let i = 0; i < ts.num / 3; i++) groups.push(unit * 3);
  } else if (ts.den >= 8 && ts.num === 5) {
    groups.push(unit * 3, unit * 2);
  } else if (ts.den >= 8 && ts.num === 7) {
    groups.push(unit * 2, unit * 2, unit * 3);
  } else {
    for (let i = 0; i < ts.num; i++) groups.push(unit);
  }
  return groups;
}
